import type { FilterFn } from "@tanstack/react-table";
import { DateTime } from "luxon";

import type { LibreViewClinicPatient } from "@/models/LibreViewModel.ts";

export type LastDataKey = "lt1m" | "lt3m" | "all";

export type FilterData = {
  lastDataSince: LastDataKey;
  tirMax: number;
  tbrMin: number;
};

export const LAST_DATA_KEYS: LastDataKey[] = ["lt1m", "lt3m", "all"];

export const DEFAULT_TIR_FILTER = 70;
export const DEFAULT_TBR_FILTER = 4;
export const DEFAULT_LAST_DATA_FILTER: LastDataKey = "all";

export const DEFAULT_COLUMN_FILTERS: FilterData = {
  lastDataSince: DEFAULT_LAST_DATA_FILTER,
  tirMax: DEFAULT_TIR_FILTER,
  tbrMin: DEFAULT_TBR_FILTER,
};

// A row passes if tir ≤ tirMax OR tbr ≥ tbrMin. Rows missing both values are hidden.
export const filterLogic = (item: LibreViewClinicPatient, filterValue: FilterData) => {
  const tir = item.tir;
  const tbr = item.tbr;
  const lastDataSince = item.lastDataSince;

  const tirMax = filterValue.tirMax;
  const tbrMin = filterValue.tbrMin;
  const lastDataSinceFilter = filterValue.lastDataSince;

  const tirPass = tir !== undefined && tir <= tirMax;
  const tbrPass = tbr !== undefined && tbr >= tbrMin;
  let lastDataPass = lastDataSince === undefined;
  if (lastDataSince) {
    const now = DateTime.now();
    const date = DateTime.fromISO(lastDataSince);
    switch (lastDataSinceFilter) {
      case "lt1m":
        lastDataPass = date > now.minus({ months: 1 });
        break;
      case "lt3m":
        lastDataPass = date > now.minus({ months: 3 });
        break;
      case "all":
        lastDataPass = true;
        break;
      default:
        lastDataPass = false;
        break;
    }
  }

  return lastDataPass && (tirPass || tbrPass);
};

export const tableFilterFn: FilterFn<LibreViewClinicPatient> = (row, _columnId, filterValue: FilterData) => {
  return filterLogic(row.original, filterValue);
};
